import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Loader2,
  CalendarPlus,
  X,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useSlotConfiguration, useFacilityData, useBookings } from '../../hooks/booking/useBookingContextSlices';
import { useAuth } from '../../context/AuthContext';
import { MODULES } from '../../constants/modules';
import { resolveGroundServiceIds } from '../../pages/bookingRouteResolver';
import { resolveSlotPrice } from '../../hooks/booking/bookingUtils';
import { configurationsAPI } from '../../services/api';
import { buildDayRows, filterRowsByStatus, getDayLoad, getCustomerLabel, toDateKey } from './agendaUtils';
import { slotDisplayName } from '../../utils/slotName';
import { ScorecardBookingCard, FreeSlotChip, EmptyState } from '../../components/ui';

const STATUS_CHIPS = [
  { id: 'all', label: 'All' },
  { id: 'booked', label: 'Booked' },
  { id: 'free', label: 'Free' },
  { id: 'due', label: 'Due' },
];

const DOT_CLASS = {
  none: 'bg-ga-text-subtle/40',
  free: 'bg-ga-primary',
  partial: 'bg-ga-warning',
  full: 'bg-ga-danger',
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function DateStrip({ days, selectedDate, onSelect, dayLoad, onExtendBack, onExtendForward, onVisibleDateChange }) {
  const stripRef = useRef(null);
  const selectedRef = useRef(null);
  const prevWidth = useRef(0);
  const keepPosition = useRef(false); // preserve scroll when prepending past days
  const extending = useRef(false);    // throttle edge triggers between renders

  useEffect(() => {
    // Keep the selected day in view (centers today on first render, and when
    // the user jumps to a date via the month picker).
    selectedRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [selectedDate]);

  // After the day list grows, keep the viewport anchored if we prepended days.
  useLayoutEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    if (keepPosition.current) {
      el.scrollLeft += el.scrollWidth - prevWidth.current;
      keepPosition.current = false;
    }
    prevWidth.current = el.scrollWidth;
    extending.current = false;
  }, [days]);

  const handleScroll = (e) => {
    const el = e.currentTarget;

    // Report the date centered in the viewport so the month/year header tracks
    // scrolling even when nothing is selected.
    if (onVisibleDateChange && days.length > 0 && el.scrollWidth > 0) {
      const step = el.scrollWidth / days.length;
      const idx = Math.min(
        days.length - 1,
        Math.max(0, Math.floor((el.scrollLeft + el.clientWidth / 2) / step))
      );
      onVisibleDateChange(days[idx]);
    }

    if (extending.current) return;
    if (el.scrollLeft < 240) {
      extending.current = true;
      keepPosition.current = true;
      prevWidth.current = el.scrollWidth;
      onExtendBack?.();
    } else if (el.scrollLeft + el.clientWidth > el.scrollWidth - 240) {
      extending.current = true;
      onExtendForward?.();
    }
  };

  return (
    <div
      ref={stripRef}
      onScroll={handleScroll}
      className="flex gap-1.5 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch]"
      style={{ scrollSnapType: 'x mandatory' }}
      role="tablist"
      aria-label="Select date"
    >
      {days.map((day) => {
        const selected = isSameDay(day, selectedDate);
        return (
          <button
            key={day.toISOString()}
            ref={selected ? selectedRef : undefined}
            type="button"
            role="tab"
            aria-selected={selected}
            style={{ scrollSnapAlign: 'center' }}
            onClick={() => onSelect(day)}
            className={`flex min-w-[3.25rem] flex-col items-center rounded-ga-md border px-2 py-1.5 transition-colors md:min-w-[4rem] ${
              selected
                ? 'border-ga-primary bg-ga-primary text-ga-text-on-primary'
                : isToday(day)
                ? 'border-ga-primary/40 bg-ga-primary-soft text-ga-primary'
                : 'border-ga-border-subtle bg-ga-bg-surface text-ga-text-primary hover:bg-ga-bg-surface-subtle'
            }`}
          >
            <span className="text-[10px] font-medium uppercase">{format(day, 'EEE')}</span>
            <span className="text-base font-bold leading-tight">{format(day, 'd')}</span>
            <span
              className={`mt-0.5 h-1.5 w-1.5 rounded-full ${
                selected
                  ? 'bg-ga-text-on-primary'
                  : DOT_CLASS[dayLoad(day)] || DOT_CLASS.none
              }`}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
}

function MonthPickerOverlay({ initialDate, onPick, onClose }) {
  const [viewMonth, setViewMonth] = useState(startOfMonth(initialDate));
  const days = eachDayOfInterval({ start: startOfMonth(viewMonth), end: endOfMonth(viewMonth) });
  const leadingBlanks = startOfMonth(viewMonth).getDay();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Jump to date"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-ga-lg border border-ga-border-subtle bg-ga-bg-surface p-4 shadow-ga-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            className="rounded-ga-full p-1.5 text-ga-text-primary hover:bg-ga-bg-surface-subtle"
            aria-label="Previous month"
            onClick={() => setViewMonth((m) => addMonths(m, -1))}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-sm font-semibold text-ga-text-primary">{format(viewMonth, 'MMMM yyyy')}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="rounded-ga-full p-1.5 text-ga-text-primary hover:bg-ga-bg-surface-subtle"
              aria-label="Next month"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <button
              type="button"
              className="rounded-ga-full p-1.5 text-ga-text-primary hover:bg-ga-bg-surface-subtle"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <span key={`${d}${i}`} className="text-[10px] font-semibold text-ga-text-subtle">
              {d}
            </span>
          ))}
          {Array.from({ length: leadingBlanks }).map((_, i) => (
            <span key={`blank-${i}`} />
          ))}
          {days.map((day) => (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onPick(day)}
              className={`rounded-ga-sm py-1.5 text-sm hover:bg-ga-primary-soft ${
                isToday(day)
                  ? 'font-bold text-ga-primary'
                  : isSameMonth(day, viewMonth)
                  ? 'text-ga-text-primary'
                  : 'text-ga-text-subtle'
              }`}
            >
              {format(day, 'd')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


function SlotRow({ row, onBook }) {
  const { slot, service, status, bookings, payment } = row;
  const time = `${slot.startTime || '--:--'}–${slot.endTime || '--:--'}`;
  const groundLabel = bookings[0]?.groundName || '';

  if (status === 'available') {
    return (
      <div className="flex items-center gap-3 rounded-ga-md border border-dashed border-ga-primary/40 bg-ga-bg-surface p-3">
        <div className="w-24 shrink-0 md:w-28">
          <p className="text-sm font-bold text-ga-text-primary">{slotDisplayName(slot.startTime, slot.endTime)}</p>
          <p className="truncate text-xs text-ga-text-muted">{time}</p>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-ga-text-muted">{service.name}</p>
        </div>
        <FreeSlotChip onClick={onBook} className="shrink-0">
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Book{row.price > 0 ? ` ${formatINR(row.price)}` : ''}
        </FreeSlotChip>
      </div>
    );
  }

  const isFull = status === 'booked';
  return (
    <ScorecardBookingCard
      title={getCustomerLabel(bookings)}
      service={service.name}
      ground={groundLabel}
      time={`${slotDisplayName(slot.startTime, slot.endTime)} · ${time}`}
      status={isFull ? 'booked' : 'partial'}
      amount={payment.due > 0 ? `Due ${formatINR(payment.due)}` : `Paid ${formatINR(payment.paid)}`}
      paymentStatus={payment.due > 0 ? 'pending' : 'paid'}
      actions={
        !isFull && (
          <FreeSlotChip onClick={onBook} size="sm">
            <Plus className="mr-1 h-3 w-3" aria-hidden="true" />
            Book
          </FreeSlotChip>
        )
      }
    >
      {!isFull && <p className="mt-2 text-xs font-medium text-ga-warning">1 side booked</p>}
    </ScorecardBookingCard>
  );
}

export default function BookingAgenda() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { slotConfiguration, updateSlotConfiguration } = useSlotConfiguration();
  const { grounds, loading: facilityLoading } = useFacilityData();
  const { bookings, loading: bookingsLoading, reloadBookings } = useBookings();

  // Reload fresh data from the server whenever this page is visited.
  useEffect(() => { reloadBookings(); }, []); // run once on mount

  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [groundFilter, setGroundFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() =>
    STATUS_CHIPS.some((c) => c.id === searchParams.get('status')) ? searchParams.get('status') : 'all'
  );
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState(null);

  const serviceConfigs = useMemo(
    () => slotConfiguration?.services || [],
    [slotConfiguration]
  );
  const dateRange = slotConfiguration?.dateRange || null;
  const loading = facilityLoading || bookingsLoading;

  // Distinct services for the selector bar. The calendar shows ONE service at a
  // time (default = the primary ground booking) to avoid the flat, confusing
  // "every service stacked together" view.
  const services = useMemo(() => {
    const map = new Map();
    serviceConfigs.forEach((cfg) => {
      const s = cfg.service || {};
      if (s.id != null && !map.has(s.id)) {
        map.set(s.id, { id: s.id, name: s.name, type: s.type, category: s.category });
      }
    });
    return Array.from(map.values());
  }, [serviceConfigs]);

  const [serviceFilter, setServiceFilter] = useState(null);

  // Default to the main ground-booking service (full-ground / team sport),
  // falling back to the first service once configs load.
  useEffect(() => {
    if (serviceFilter != null || services.length === 0) return;
    const ground = services.find(
      (s) => s.type === 'cricket_match' || s.category === 'team_sports'
    );
    setServiceFilter(ground?.id ?? services[0].id);
  }, [services, serviceFilter]);


  // Date strip range grows as the user scrolls toward either edge, so they can
  // browse arbitrarily far back or forward. Starts at ±30 days around today.
  const [daysBack, setDaysBack] = useState(30);
  const [daysForward, setDaysForward] = useState(30);
  // Month/year shown in the header follows whatever is scrolled into view.
  const [visibleDate, setVisibleDate] = useState(selectedDate);
  const extendBack = useCallback(() => setDaysBack((d) => d + 30), []);
  const extendForward = useCallback(() => setDaysForward((d) => d + 30), []);

  const stripDays = useMemo(() => {
    const today = startOfDay(new Date());
    // Always include a date jumped to via the month picker, even if far away.
    const start = selectedDate < addDays(today, -daysBack) ? selectedDate : addDays(today, -daysBack);
    const end = selectedDate > addDays(today, daysForward) ? selectedDate : addDays(today, daysForward);
    return eachDayOfInterval({ start, end });
  }, [selectedDate, daysBack, daysForward]);

  const dayLoad = useMemo(() => {
    const cache = new Map();
    return (day) => {
      const key = toDateKey(day);
      if (!cache.has(key)) {
        cache.set(
          key,
          getDayLoad({ date: day, serviceConfigs, bookings, groundId: groundFilter })
        );
      }
      return cache.get(key);
    };
  }, [serviceConfigs, bookings, groundFilter]);

  const dayRows = useMemo(
    () => buildDayRows({ date: selectedDate, serviceConfigs, bookings, groundId: groundFilter }),
    [selectedDate, serviceConfigs, bookings, groundFilter]
  );

  const rowsWithPrice = useMemo(
    () =>
      dayRows.map((row) => ({
        ...row,
        price: (() => {
          // Always try day-specific price first so variations apply
          const dayPrice = resolveSlotPrice({
            serviceConfig: row.serviceConfig,
            slotId: row.slot?.id,
            date: selectedDate,
          });
          if (dayPrice != null && dayPrice > 0) return dayPrice;
          // Fall back to slot-level defaults
          if (parseFloat(row.slot?.price) > 0) return parseFloat(row.slot.price);
          if (parseFloat(row.slot?.defaultPrice) > 0) return parseFloat(row.slot.defaultPrice);
          return 0;
        })(),
      })),
    [dayRows, selectedDate]
  );

  const serviceRows = useMemo(
    () =>
      serviceFilter == null
        ? rowsWithPrice
        : rowsWithPrice.filter((r) => Number(r.service?.id) === Number(serviceFilter)),
    [rowsWithPrice, serviceFilter]
  );

  const visibleRows = filterRowsByStatus(serviceRows, statusFilter);

  const configIncomplete = !dateRange || serviceConfigs.length === 0;
  const beyondWindow =
    dateRange && toDateKey(selectedDate) > toDateKey(new Date(dateRange.endDate));

  const handleBook = (row) => {
    const resolved = resolveGroundServiceIds(grounds || [], row.service || {});
    if (!resolved?.groundId || !resolved?.serviceId) {
      window.alert('Unable to open booking form for this service. Please refresh configuration and try again.');
      return;
    }
    // The full-team form (two sides / both-teams) is reserved for the cricket
    // ground match. Every other service — open nets, practice nets and all
    // hourly/individual bookings — uses the simple individual/group form.
    const isCricketGroundMatch = row.service?.type === 'cricket_match';
    const bookingRoute = isCricketGroundMatch
      ? `/dashboard/booking/${resolved.groundId}/${resolved.serviceId}`
      : `/dashboard/booking-new/${resolved.groundId}/${resolved.serviceId}`;

    navigate(bookingRoute, {
      state: {
        preselectedDate: selectedDate,
        preselectedSlot: row.slot,
        preselectedPrice: row.price,
        existingBookings: row.bookings || [],
        isEditMode: false,
        serviceName: row.service?.name,
        serviceConfig: row.serviceConfig,
      },
    });
  };

  const handleExtend = async () => {
    const target = toDateKey(selectedDate);
    setExtending(true);
    setExtendError(null);
    try {
      const res = await configurationsAPI.extendDateRange(target);
      if (!res?.success) throw new Error(res?.message || 'Failed to extend');
      updateSlotConfiguration({
        ...slotConfiguration,
        dateRange: { ...dateRange, endDate: new Date(`${target}T00:00:00`) },
      });
    } catch (err) {
      setExtendError(err.message || 'Failed to extend booking window');
    } finally {
      setExtending(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header: month button opens the jump-to-date overlay */}
        <div className="flex items-center justify-between">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-ga-text-primary">
            <Calendar className="h-6 w-6 text-ga-primary" aria-hidden="true" />
            Bookings
          </h1>
          <button
            type="button"
            onClick={() => setMonthPickerOpen(true)}
            className="flex items-center gap-1 rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-3 py-1.5 text-sm font-semibold text-ga-text-primary hover:bg-ga-bg-surface-subtle"
          >
            {format(selectedDate, 'MMMM yyyy')}
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <DateStrip
          days={stripDays}
          selectedDate={selectedDate}
          onSelect={setSelectedDate}
          dayLoad={dayLoad}
          onExtendBack={extendBack}
          onExtendForward={extendForward}
        />

        {/* Ground chips (only useful with more than one ground) */}
        {(grounds || []).length > 1 && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setGroundFilter(null)}
              className={`rounded-ga-full px-3 py-1.5 text-xs font-semibold ${
                groundFilter === null
                  ? 'bg-ga-primary text-ga-text-on-primary'
                  : 'border border-ga-border-subtle bg-ga-bg-surface text-ga-text-primary hover:bg-ga-bg-surface-subtle'
              }`}
            >
              All grounds
            </button>
            {grounds.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setGroundFilter(g.id)}
                className={`rounded-ga-full px-3 py-1.5 text-xs font-semibold ${
                  Number(groundFilter) === Number(g.id)
                    ? 'bg-ga-primary text-ga-text-on-primary'
                    : 'border border-ga-border-subtle bg-ga-bg-surface text-ga-text-primary hover:bg-ga-bg-surface-subtle'
                }`}
              >
                {g.name}
              </button>
            ))}
          </div>
        )}

        {/* Service selector bar — one service's calendar at a time */}
        {services.length > 1 && (
          <div
            className="flex gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]"
            role="tablist"
            aria-label="Select service"
          >
            {services.map((s) => {
              const active = Number(serviceFilter) === Number(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setServiceFilter(s.id)}
                  className={`shrink-0 rounded-ga-sm px-4 py-2 text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-ga-primary text-ga-text-on-primary shadow-ga-soft'
                      : 'border border-ga-border-subtle bg-ga-bg-surface text-ga-text-primary hover:bg-ga-bg-surface-subtle'
                  }`}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Status chips */}
        <div className="flex gap-2" role="tablist" aria-label="Filter slots">
          {STATUS_CHIPS.map((chip) => (
            <button
              key={chip.id}
              type="button"
              role="tab"
              aria-selected={statusFilter === chip.id}
              onClick={() => setStatusFilter(chip.id)}
              className={`rounded-ga-full px-3.5 py-1.5 text-xs font-semibold ${
                statusFilter === chip.id
                  ? 'bg-ga-primary text-ga-text-on-primary'
                  : 'border border-ga-border-subtle bg-ga-bg-surface text-ga-text-primary hover:bg-ga-bg-surface-subtle'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Inline config banner — calendar is never gated */}
        {configIncomplete && !loading && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Finish slot setup to enable booking
            </p>
            {hasPermission(MODULES.CONFIGURATION, 'write') && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/configure-service')}
                className="shrink-0 text-sm font-semibold text-amber-800 underline underline-offset-2 dark:text-amber-200"
              >
                Set up →
              </button>
            )}
          </div>
        )}

        {/* Selected date beyond the configured window — offer extend inline */}
        {!configIncomplete && beyondWindow && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Slots are configured until {format(new Date(dateRange.endDate), 'dd MMM yyyy')}.
            </p>
            {hasPermission(MODULES.CONFIGURATION, 'write') && (
              <button
                type="button"
                disabled={extending}
                onClick={handleExtend}
                className="flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
              >
                {extending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                )}
                Extend to {format(selectedDate, 'dd MMM')}
              </button>
            )}
            {extendError && (
              <p className="w-full text-xs text-red-600">{extendError}</p>
            )}
          </div>
        )}

        {/* Slot list */}
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700" />
            ))}
          </div>
        ) : visibleRows.length > 0 ? (
          <div className="space-y-2">
            {visibleRows.map((row) => (
              <SlotRow key={row.key} row={row} onBook={() => handleBook(row)} />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white py-10 text-center dark:border-gray-700 dark:bg-gray-800">
            <Calendar className="mx-auto mb-2 h-10 w-10 text-gray-300" aria-hidden="true" />
            <p className="text-sm text-gray-500">
              {serviceRows.length > 0
                ? 'No slots match this filter.'
                : `No slots for ${format(selectedDate, 'EEEE, dd MMM')}.`}
            </p>
          </div>
        )}
      </div>

      {monthPickerOpen && (
        <MonthPickerOverlay
          initialDate={selectedDate}
          onPick={(day) => {
            setSelectedDate(startOfDay(day));
            setMonthPickerOpen(false);
          }}
          onClose={() => setMonthPickerOpen(false)}
        />
      )}
    </DashboardLayout>
  );
}
